import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import { connect } from "react-redux";
import { ChevronRight, Home } from "lucide-react";

/**
 * Breadcrumbs component - Shows current navigation path
 * Provides context to users about their location in the app
 */
const BreadcrumbsBase = ({ category, location, machine }) => {

  // Map of paths to readable labels
  const pathLabels = useMemo(() => ({
    // Acciones
    '/hotelrental': 'Montarto',
    '/lockersale': 'Vender',
    '/pin': 'Recuperar PIN',
    '/supportticketadd': 'Nuevo Ticket',
    
    // Canales
    '/machines': category === 'BAQUEIRA' ? 'Controladores' : 'Kioscos',
    '/qrs': 'QRs',
    '/pdas': 'PDAs',
    '/operators': 'Agregadores',
    
    // Gestión
    '/rentals': 'Reservas',
    '/calendars': 'Calendarios',
    '/redeemcodes': 'Redeem Codes',
    '/email-templates': 'Plantillas de Email',
    '/email-template': 'Plantilla de Email',
    '/partners': 'Partners',
    '/discount-codes': 'Códigos de Descuento',
    
    // Reporte
    '/operational-dashboard': 'Dashboard Operativo',
    '/dashboard-rental': 'Dashboard Reservas',
    '/pro-dashboard': 'Dashboard PRO',
    '/activity-dashboard': 'Dashboard Actividad',
    '/occupancy-dashboard': 'Dashboard Ocupación',
    '/arqueos-dashboard': 'Dashboard Arqueos',
    '/sales': 'Ventas',
    '/internalsales': 'Análisis por tipo',
    '/online-sales': 'Ventas Online',
    '/arqueos': 'Gráficos',
    '/arqueossales': 'KPI',
    '/arqueoscompare': 'Comparativa',
    '/withdrawals': 'Retiros',
    '/ocupation': 'Ocupación',
    '/ocupationaccumulated': 'Ocupación acumulada',
    '/ocupationdaily': 'Ocupación diaria',
    '/ocupationdetailed': 'Ocupación detallada',
    '/objectives': 'Objetivos',
    '/localizacion': 'Localización',
    '/batteries': 'Baterías',
    '/software-versions': 'Versiones SW',
    
    // Operativa
    '/supporttickets': 'Tickets',
    '/faults': 'Averías',
    '/incidences': 'Incidencias',
    '/slas': 'SLA',
    '/audit': 'Audit',
    '/tpvs': 'TPVs',
    '/modules': 'Módulos',
    '/matrixs': 'Firmware',
    '/lowbluetooth': 'Bluetooth',
    '/lockersqty': 'Lockers QTY',
    '/openings': 'Aperturas',
    '/pinrecovers': 'Recuperación PINes',
    '/universalupdates': 'Actualizaciones',
    '/maint-planner': 'Planner Mantenimiento',
    '/bleconnection': 'Conexión BLE',
    '/change-sn': 'Cambio SN',
    '/shifts': 'Turnos',
    '/rentalsconflicts': 'Conflictos Reservas',
    '/salesconflicts': 'Conflictos Ventas',
    
    // Baqueira
    '/rules': 'Reglas de negocio',
    '/articles': 'Artículos',
    '/arqueosbaqueira': 'Arqueos ERP Baqueira',
    '/sales-modes': 'Modos de Venta',
    '/sales-modes-quiz': 'Quiz Modos de Venta',
    
    // Soporte
    '/support-chat': 'Chat',
    '/faqs': 'FAQs',
    '/manuals': 'Manuales',
    '/integraciones': 'Integraciones',
    '/api': 'API',
    '/planes': 'Planes',
    '/ar-viewer': 'Visor AR',
    
    // Propuestas
    '/proposal-configurator': 'Configurador',
    '/proposals': 'Propuestas',
    '/proposal-prices': 'Precios',
    
    // Usuarios
    '/my-cash-users': 'Usuarios Cash-Control',
    '/my-pda-users': 'Usuarios Operativo',
    '/my-manager-users': 'Usuarios Manager',
    
    // Admin
    '/customers': 'Clientes',
    '/contracts': 'Contratos',
    '/promoters': 'Promotores',
    '/logistics': 'Logística',
    '/gameranking': 'Juegos',
    '/replacements': 'Recambios',
    '/users': 'Administradores',
    '/ai-generator': 'Generador IA',
  }), [category]);

  // Map of paths to their parent categories
  const pathCategories = useMemo(() => ({
    // Acciones
    '/hotelrental': 'Acciones',
    '/lockersale': 'Acciones',
    '/pin': 'Acciones',
    '/supportticketadd': 'Acciones',
    
    // Canales
    '/machines': 'Canales',
    '/qrs': 'Canales',
    '/pdas': 'Canales',
    
    // Gestión
    '/rentals': 'Gestión',
    '/calendars': 'Gestión',
    '/redeemcodes': 'Gestión',
    '/email-templates': 'Gestión',
    '/email-template': 'Gestión',
    '/partners': 'Gestión',
    '/discount-codes': 'Gestión',
    
    // Reporte
    '/operational-dashboard': 'Reporte',
    '/dashboard-rental': 'Reporte',
    '/pro-dashboard': 'Reporte',
    '/activity-dashboard': 'Reporte',
    '/occupancy-dashboard': 'Reporte',
    '/arqueos-dashboard': 'Reporte',
    '/sales': 'Reporte',
    '/internalsales': 'Reporte',
    '/online-sales': 'Reporte',
    '/arqueos': 'Reporte',
    '/arqueossales': 'Reporte',
    '/arqueoscompare': 'Reporte',
    '/withdrawals': 'Reporte',
    '/ocupation': 'Reporte',
    '/ocupationaccumulated': 'Reporte',
    '/ocupationdaily': 'Reporte',
    '/ocupationdetailed': 'Reporte',
    '/objectives': 'Reporte',
    '/localizacion': 'Reporte',
    '/batteries': 'Reporte',
    '/software-versions': 'Reporte',
    
    // Operativa
    '/supporttickets': 'Operativa',
    '/faults': 'Operativa',
    '/incidences': 'Operativa',
    '/slas': 'SAT',
    '/audit': 'Operativa',
    '/tpvs': 'Operativa',
    '/modules': 'Operativa',
    '/matrixs': 'Operativa',
    '/lowbluetooth': 'Operativa',
    '/lockersqty': 'Operativa',
    '/openings': 'Operativa',
    '/pinrecovers': 'Reporte',
    '/universalupdates': 'Operativa',
    '/maint-planner': 'Operativa',
    '/bleconnection': 'Operativa',
    '/change-sn': 'Operativa',
    '/shifts': 'Operativa',
    '/rentalsconflicts': 'Gestión',
    '/salesconflicts': 'Gestión',
    
    // Baqueira
    '/operators': 'Baqueira',
    '/rules': 'Baqueira',
    '/articles': 'Baqueira',
    '/arqueosbaqueira': 'Baqueira',
    '/sales-modes': 'Baqueira',
    '/sales-modes-quiz': 'Baqueira',
    
    // Soporte
    '/support-chat': 'Soporte',
    '/faqs': 'Soporte',
    '/manuals': 'Soporte',
    '/integraciones': 'Soporte',
    '/api': 'Reporte',
    '/planes': 'Soporte',
    '/ar-viewer': 'Soporte',
    
    // Propuestas
    '/proposal-configurator': 'Propuestas',
    '/proposals': 'Propuestas',
    '/proposal-prices': 'Propuestas',
    
    // Usuarios
    '/my-cash-users': 'Usuarios',
    '/my-pda-users': 'Usuarios',
    '/my-manager-users': 'Usuarios',
    
    // Admin
    '/customers': 'Administración',
    '/contracts': 'Administración',
    '/promoters': 'Administración',
    '/logistics': 'Administración',
    '/gameranking': 'Administración',
    '/replacements': 'Administración',
    '/users': 'Administración',
    '/ai-generator': 'Administración',
  }), []);

  // Build breadcrumb items from current path
  const breadcrumbs = useMemo(() => {
    const pathname = location.pathname;
    const items = [];

    // Always add home
    items.push({
      label: 'Inicio',
      path: '/',
      icon: Home,
    });

    // Check if we're on home page
    if (pathname === '/') {
      return items;
    }

    // Get base path (without ID parameters)
    const pathParts = pathname.split('/').filter(Boolean);
    const basePath = `/${pathParts[0]}`;
    
    // Add category if exists
    const categoryName = pathCategories[basePath];
    if (categoryName) {
      items.push({
        label: categoryName,
        path: null, // Categories are not clickable
        icon: null,
      });
    }

    // Add current page
    const pageLabel = pathLabels[basePath];
    if (pageLabel) {
      items.push({
        label: pageLabel,
        path: basePath,
        icon: null,
      });
    }

    // If there's an ID in the URL (like /machines/123)
    if (pathParts.length > 1) {
      const id = pathParts[pathParts.length - 1];
      // Only show if it looks like an ID (not another route segment)
      if (id.length > 3 && !/^[a-z]+$/.test(id)) {
        // Use machine Serial when on a machine detail page
        const detailLabel = basePath === '/machines' && machine && machine.Serial
          ? machine.Serial
          : 'Detalle';
        items.push({
          label: detailLabel,
          path: pathname,
          icon: null,
        });
      }
    }

    return items;
  }, [location.pathname, pathLabels, pathCategories, machine]);

  // Don't render if we only have home (we're on home page)
  if (breadcrumbs.length <= 1) {
    return null;
  }

  return (
    <nav className="breadcrumbs" aria-label="Breadcrumb">
      <ol className="breadcrumbs-list">
        {breadcrumbs.map((crumb, index) => {
          const isLast = index === breadcrumbs.length - 1;
          const Icon = crumb.icon;

          return (
            <li key={index} className="breadcrumbs-item">
              {crumb.path && !isLast ? (
                <Link 
                  to={crumb.path} 
                  className="breadcrumbs-link"
                  aria-label={`Ir a ${crumb.label}`}
                >
                  {Icon && <Icon size={14} className="breadcrumbs-icon" />}
                  <span>{crumb.label}</span>
                </Link>
              ) : (
                <span 
                  className={`breadcrumbs-text ${isLast ? 'breadcrumbs-current' : ''}`}
                  aria-current={isLast ? 'page' : undefined}
                >
                  {Icon && <Icon size={14} className="breadcrumbs-icon" />}
                  <span>{crumb.label}</span>
                </span>
              )}
              {!isLast && (
                <ChevronRight 
                  size={14} 
                  className="breadcrumbs-separator"
                  aria-hidden="true"
                />
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
};

const mapStateToProps = (state) => ({
  location: state.router ? state.router.location : { pathname: '/' },
  machine: state.machines ? state.machines.machine : null,
});

const Breadcrumbs = connect(mapStateToProps)(BreadcrumbsBase);

export default Breadcrumbs;
